import { ReactNode } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard, Progress, WarningPanel } from '@backstage/core-components';
import { Chip, Grid, Typography, makeStyles } from '@material-ui/core';
import { useVaultRequest } from '../../hooks/useVaultRequest';
import { useLiveUpdates } from '../../hooks/useLiveUpdates';
import type { VaultRequestState } from '../../api/VaultApi';
import { stateLabel } from '../../lib/requestStateLabels';

// Text comes from the shared stateLabel() (lib/requestStateLabels.ts) —
// this map is colors only, not wording, per that lib's own design (colors/
// styling stay local to each component; text is the single source of
// truth). Still keyed by the full VaultRequestState so a state TypeScript
// doesn't know a color for fails to compile, rather than silently falling
// through to the generic default below.
const STATE_COLORS: Record<VaultRequestState, { bg: string; color: string }> = {
  submitted: { bg: '#fff3e0', color: '#e65100' },
  pending_approval: { bg: '#fff3e0', color: '#e65100' },
  rejected_approval: { bg: '#fce4ec', color: '#b71c1c' },
  // Same amber-ish hue as pending_approval — still "in the approval
  // pipeline," just diverted to a standard-change path.
  standard_redirect: { bg: '#fff3e0', color: '#e65100' },
  approved: { bg: '#e8f5e9', color: '#2e7d32' },
  pr_open: { bg: '#fff8e1', color: '#f57f17' },
  // SA Rotation only — approved and waiting for its change window.
  scheduled: { bg: '#e3f2fd', color: '#1565c0' },
  deploying: { bg: '#e3f2fd', color: '#1565c0' },
  deployed: { bg: '#e8f5e9', color: '#1b5e20' },
  completed: { bg: '#e8f5e9', color: '#1b5e20' },
  deploy_failed: { bg: '#fce4ec', color: '#c62828' },
  cleanup_failed: { bg: '#fce4ec', color: '#c62828' },
  stuck: { bg: '#fff3e0', color: '#bf360c' },
  abandoned: { bg: '#f5f5f5', color: '#616161' },
  // Same family as `abandoned` — a closed-without-progressing outcome.
  pr_dropped: { bg: '#f5f5f5', color: '#616161' },
  gate2_denied: { bg: '#fce4ec', color: '#b71c1c' },
  // The other SNOW intermediate-milestone states are deliberately absent
  // here (and from VaultRequestState) — per docs/mvp/07-open-questions.md
  // and GH #598, ServiceNow only ever pushes the terminal approved/
  // rejected decision, so these can never actually be driven by a real
  // SNOW signal.
  // Warning-toned like `stuck`, but deliberately distinct: `stuck` is a
  // terminal dead-letter state (deep-orange-900 text on an amber-50 bg),
  // while snow_stalled is non-terminal (dwell timer still running, may
  // recover on its own). Same deep-orange family, one shade lighter text
  // and a redder-tinted bg so the two are visually distinguishable at a
  // glance.
  snow_stalled: { bg: '#fbe9e7', color: '#d84315' },
  // A distinct family from snow_stalled's red/orange, so the two dwell
  // states aren't confusable at a glance.
  gitops_stalled: { bg: '#f3e5f5', color: '#6a1b9a' },
  decommission_pending: { bg: '#fff3e0', color: '#e65100' },
  // Same red family as deploy_failed — some clusters exhausted retries.
  deploy_interrupted: { bg: '#fce4ec', color: '#c62828' },
  // Same grey family as abandoned/pr_dropped — a closed-without-progressing
  // outcome (superseded by a newer edit request, not a failure).
  superseded: { bg: '#f5f5f5', color: '#616161' },
};

const useStyles = makeStyles(theme => ({
  label: { color: theme.palette.text.secondary, fontSize: 11, marginBottom: 2 },
  value: { fontSize: 13 },
  grid: { marginTop: theme.spacing(1) },
  empty: { color: theme.palette.text.disabled, fontStyle: 'italic' },
}));

// request.environment is a scalar string in practice today (one manifest per
// environment), but the type also allows string[] for forward compatibility
// with a future multi-environment manifest — handle both.
function formatEnvironment(environment: string | string[]): string {
  return Array.isArray(environment) ? environment.join(', ') : environment;
}

// Renders a SNOW identifier (CHG / RITM / AR) as a link to the request's SNOW
// ticket when a URL is available, otherwise plain text.
function renderSnowId(
  id?: string | null,
  ticketUrl?: string | null,
): ReactNode {
  if (!id) return null;
  if (!ticketUrl) return id;
  return (
    <a href={ticketUrl} target="_blank" rel="noreferrer">
      {id}
    </a>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  const classes = useStyles();
  return (
    <div>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.value}>
        {value || <span className={classes.empty}>—</span>}
      </Typography>
    </div>
  );
}

export function EntityVaultRequestOverviewCard() {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};
  const requestId = annotations['vault.ice.com/request-id'];
  const { request, loading, error, reload } = useVaultRequest(requestId);
  const classes = useStyles();
  useLiveUpdates(requestId ? `vault:request:${requestId}` : undefined, reload);

  if (loading) return <Progress />;
  if (error)
    return (
      <WarningPanel
        title="Failed to load vault request"
        message={error.message}
      />
    );
  if (!request) return <WarningPanel title="Request not found" />;

  const stateStyle = STATE_COLORS[request.state] ?? {
    bg: '#f5f5f5',
    color: '#333',
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6}>
        <InfoCard title="Request Status">
          <Chip
            label={stateLabel(request.state)}
            style={{
              background: stateStyle.bg,
              color: stateStyle.color,
              fontWeight: 700,
            }}
          />
          <Grid container spacing={1} className={classes.grid}>
            <Grid item xs={6}>
              <Field
                label="Request ID"
                value={<code>{request.requestId.slice(0, 8)}</code>}
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Submitted"
                value={new Date(request.generatedAt).toLocaleDateString()}
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="CHG Number"
                value={renderSnowId(request.chgNumber, request.ticketUrl)}
              />
            </Grid>
            {request.ritmNumber && (
              <Grid item xs={6}>
                <Field
                  label="RITM"
                  value={renderSnowId(request.ritmNumber, request.ticketUrl)}
                />
              </Grid>
            )}
            {request.arNumber && (
              <Grid item xs={6}>
                <Field
                  label="AR"
                  value={renderSnowId(request.arNumber, request.ticketUrl)}
                />
              </Grid>
            )}
            <Grid item xs={6}>
              <Field
                label="PR"
                value={
                  request.prUrl ? (
                    <a href={request.prUrl} target="_blank" rel="noreferrer">
                      View PR
                    </a>
                  ) : null
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Review Date"
                value={new Date(request.reviewDate).toLocaleDateString()}
              />
            </Grid>
            <Grid item xs={6}>
              <Field label="Approver" value={request.approver} />
            </Grid>
            {request.awxJobId && (
              <Grid item xs={6}>
                <Field
                  label="AAP Job"
                  value={<code>{request.awxJobId}</code>}
                />
              </Grid>
            )}
            {request.deployedAt && (
              <Grid item xs={6}>
                <Field
                  label="Deployed"
                  value={new Date(request.deployedAt).toLocaleDateString()}
                />
              </Grid>
            )}
            {request.rejectionReason && (
              <Grid item xs={12}>
                <Field
                  label="Rejection Reason"
                  value={request.rejectionReason}
                />
              </Grid>
            )}
          </Grid>
        </InfoCard>
      </Grid>
      <Grid item xs={12} sm={6}>
        <InfoCard title="Request Details">
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Field label="BSN" value={request.bsn} />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Environment"
                value={formatEnvironment(request.environment)}
              />
            </Grid>
            <Grid item xs={6}>
              <Field label="Application Type" value={request.applicationType} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Access Model" value={request.accessType} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Requester" value={request.requester} />
            </Grid>
          </Grid>
        </InfoCard>
      </Grid>
      {request.approvalTrail.length > 0 && (
        <Grid item xs={12}>
          <InfoCard title="Approval Chain">
            {request.approvalTrail.map(milestone => (
              <div
                key={milestone.stage}
                style={{
                  display: 'flex',
                  gap: 8,
                  marginBottom: 6,
                  alignItems: 'baseline',
                  flexWrap: 'wrap',
                }}
              >
                <Typography className={classes.value} style={{ minWidth: 18 }}>
                  {milestone.stage}.
                </Typography>
                <Typography
                  className={classes.value}
                  style={{ fontWeight: 600, minWidth: 170 }}
                >
                  {milestone.roleLabel}
                </Typography>
                <Chip
                  size="small"
                  label={
                    milestone.status === 'approved' ? 'Approved' : 'Pending'
                  }
                  style={
                    milestone.status === 'approved'
                      ? { background: '#e8f5e9', color: '#2e7d32' }
                      : { background: '#fff3e0', color: '#e65100' }
                  }
                />
                {milestone.approver && (
                  <Typography className={classes.label}>
                    {milestone.approver}
                  </Typography>
                )}
                {milestone.timestamp && (
                  <Typography className={classes.label}>
                    {new Date(milestone.timestamp).toLocaleDateString()}
                  </Typography>
                )}
              </div>
            ))}
          </InfoCard>
        </Grid>
      )}
    </Grid>
  );
}
